<template>
  <div>
    <v-layout
      row
      align-center
      :style="myLayoutStyle"
    >
      <v-flex
        xs12
        sm10
        offset-sm1
        md10
        offset-md1
        lg10
        offset-lg1
        xl8
        offset-xl2
      >
        <v-card
          flat
          :color="theme.bg_color"
        >
          <v-card-text>
            <page-title :title="$t('reservations')" />
            <h2 :style="titleStyle">
              {{ $t('active_reservations') }}
            </h2>
            <div
              v-if="reservations && reservations.length > 0"
              class="mt-2 mb-2"
            >
              <v-card
                v-for="(reservation, index) in reservations"
                :key="reservation.id"
                style="border-radius: 5px; margin-bottom: 18px;"
                :color="theme.address_bg_color"
              >
                <v-card-title class="pb-0">
                  <v-layout row>
                    <v-flex xs12>
                      <h4 :style="cardTitleStyle">{{ $t('reservation') }} {{ index + 1 }}</h4>
                    </v-flex>
                  </v-layout>
                </v-card-title>
                <v-card-text :style="cardTextStyle">
                  <v-layout
                    row
                    pt-2
                    pb-1
                  >
                    <v-flex xs12>
                      <v-icon
                        class="middle"
                        small
                        :color="theme.address_text_color"
                      >fas fa-calendar</v-icon>
                      {{ dateTime(reservation.booking_at).date }}
                    </v-flex>
                  </v-layout>
                  <v-layout
                    row
                    py-1
                  >
                    <v-flex xs12>
                      <v-icon
                        class="middle"
                        small
                        :color="theme.address_text_color"
                      >fas fa-clock</v-icon>
                      {{ dateTime(reservation.booking_at).time }}
                    </v-flex>
                  </v-layout>
                  <v-layout
                    row
                    py-1
                  >
                    <v-flex xs12>
                      <v-icon
                        class="middle"
                        small
                        :color="theme.address_text_color"
                      >fas fa-user-alt</v-icon>
                      {{ reservation.pax }} {{ $t('people') }}
                    </v-flex>
                  </v-layout>
                  <v-layout
                    v-if="discount(reservation.preset_discount)"
                    row
                    py-1
                  >
                    <v-flex xs12>
                      <v-icon
                        class="middle"
                        small
                        :color="theme.address_text_color"
                      >fas fa-tag</v-icon>
                      {{ discount(reservation.preset_discount) }} {{ $t('discount') }}
                    </v-flex>
                  </v-layout>
                  <v-layout
                    v-if="reservation.remarks"
                    row
                    py-1
                  >
                    <v-flex xs12>
                      <span class="font-italic small">- {{ reservation.remarks }}</span>
                    </v-flex>
                  </v-layout>
                </v-card-text>
              </v-card>
            </div>
            <div
              v-else
              class="mt-2 mb-2"
              :style="nameStyle"
            >
              {{ $t('none') }}
            </div>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>
    <v-footer
      fixed
      app
      inset
      :height="theme.footer_height"
      :color="theme.footer_bg_color"
    >
      <v-btn
        block
        dark
        large
        class="btn"
        :color="theme.btn_color_primary"
      >
        <v-layout row>
          <v-flex
            xs12
            class="text-xs-center"
            @click="addReservation"
          >
            {{ $t('make_a_reservation') }}
          </v-flex>
        </v-layout>
      </v-btn>
    </v-footer>
    <v-dialog
      v-model="dialog"
      persistent
      fullscreen
    >
      <v-card :color="theme.login_bg_color">
        <v-card-text class="pa-4">
          <form
            @submit.prevent="checkReservation"
            @keydown="form.onKeydown($event)"
          >
            <v-layout row>
              <v-flex
                xs12
                sm10
                offset-sm1
                md8
                offset-md2
                lg6
                offset-lg3
                xl6
                offset-xl3
              >
                <v-layout
                  row
                  align-center
                >
                  <v-flex xs12>
                    <span class="main-title">{{ $t('add_reservation') }}</span>
                  </v-flex>
                </v-layout>
                <div class="mt-2">
                  <div>{{ $t('choose_date') }}</div>
                  <v-container
                    v-if="loadingDate"
                    fill-height
                  >
                    <v-layout row>
                      <v-flex
                        xs12
                        class="text-xs-center"
                      >
                        <v-icon small>fas fa-circle-notch fa-spin</v-icon>
                      </v-flex>
                    </v-layout>
                  </v-container>
                  <div
                    v-else-if="dates.from && dates.to"
                    class="mt-2 mb-4"
                  >
                    <datepicker
                      :style="cssVars"
                      :value="form.date"
                      inline
                      class="date-picker"
                      calendar-class="date-picker-calendar"
                      :disabled-dates="dates"
                      @selected="selectDate"
                    />
                  </div>
                  <v-container
                    v-else
                    fill-height
                  >
                    <v-layout row>
                      <v-flex
                        xs12
                        class="text-xs-center"
                      >
                        <span class="font-italic grey-color">{{ $t('no_available_slot') }}</span>
                      </v-flex>
                    </v-layout>
                  </v-container>
                  <div class="mt-2">
                    <div>{{ $t('choose_time') }}</div>
                    <v-container
                      v-if="isLoading"
                      fill-height
                    >
                      <v-layout row>
                        <v-flex
                          xs12
                          class="text-xs-center"
                        >
                          <v-icon small>fas fa-circle-notch fa-spin</v-icon>
                        </v-flex>
                      </v-layout>
                    </v-container>
                    <horizontal-scroll
                      v-else-if="times && times.length > 0"
                      class="mt-2 mb-4"
                    >
                      <div
                        v-for="(time, index) in times"
                        :key="index"
                        class="mr-2 text-xs-center"
                        :style="timeStyle(time.time)"
                        :id="index"
                        @click="selectTime(time)"
                      >
                        <v-layout column>
                          <v-flex>
                            <span class="smaller">{{ time.time }}</span>
                          </v-flex>
                          <v-flex v-if="discount(time.preset_discount)">
                            -{{ discount(time.preset_discount) }}
                          </v-flex>
                          <v-icon
                            v-else
                            small
                            class="middle"
                            :style="seedlingStyle(time.time)"
                          >fas fa-seedling</v-icon>
                        </v-layout>
                      </div>
                    </horizontal-scroll>
                    <v-container
                      v-else
                      fill-height
                    >
                      <v-layout row>
                        <v-flex
                          xs12
                          class="text-xs-center"
                        >
                          <span class="font-italic grey-color">{{ $t('no_available_slot') }}</span>
                        </v-flex>
                      </v-layout>
                    </v-container>
                  </div>
                  <v-divider class="mt-2 mb-3" />
                  <v-layout
                    row
                    align-center
                    class="mb-2"
                  >
                    <v-flex xs12>
                      <span class="main-title">{{ $t('booking_details') }}</span>
                    </v-flex>
                  </v-layout>
                  <v-layout row>
                    <v-flex
                      xs12
                      sm8
                      md8
                      lg8
                      xl8
                    >
                      <v-select
                        v-model="form.pax"
                        :items="slotPax"
                        menu-props="auto"
                        :label="$t('pax')"
                        name="pax"
                        prepend-icon="fas fa-user-alt"
                        outline
                      ></v-select>
                    </v-flex>
                  </v-layout>
                  <v-layout row>
                    <v-flex
                      xs12
                      sm8
                      md8
                      lg8
                      xl8
                    >
                      <v-textarea
                        v-model="form.remarks"
                        outline
                        hide-details
                        rows="3"
                        :label="$t('remarks')"
                        name="remarks"
                        prepend-icon="fas fa-sticky-note"
                        maxlength="100"
                      />
                    </v-flex>
                  </v-layout>
                </div>
                <div class="text-xs-center mt-4">
                  <v-btn
                    :color="theme.btn_color_secondary"
                    type="submit"
                    :disabled="isLoading"
                    :dark="!isLoading"
                  >
                    {{ $t('save') }}
                  </v-btn>
                  <v-btn
                    flat
                    :color="theme.btn_color_secondary"
                    @click="dialog = false"
                  >
                    {{ $t('close') }}
                  </v-btn>
                </div>
              </v-flex>
            </v-layout>
          </form>
        </v-card-text>
      </v-card>
      <v-dialog
        v-if="saveDialog"
        v-model="saveDialog"
        persistent
        max-width="500"
      >
        <v-card>
          <v-card-title :style="confirmTitleStyle">{{ $t('confirm_reservation') }}</v-card-title>
          <v-card-text class="pt-4 px-4">
            <v-layout
              v-if="reservationConfig.enableDeposit"
              row
              align-center
              mt-2
            >
              <v-flex
                xs4
                sm3
                md2
                lg2
                xl2
                class="text-xs-left"
              >
                {{ $t('deposit') }}
              </v-flex>
              <v-flex
                xs8
                sm9
                md10
                lg10
                xl10
                class="text-xs-left"
              >
                $ {{ reservationConfig.voucherPrice }}
              </v-flex>
            </v-layout>
            <v-layout
              row
              align-center
              mt-2
            >
              <v-flex
                xs4
                sm3
                md2
                lg2
                xl2
                class="text-xs-left"
              >
                {{ $t('date') }}
              </v-flex>
              <v-flex
                xs8
                sm9
                md10
                lg10
                xl10
                class="text-xs-left"
              >
                {{ dateTime(form.date).date }}
              </v-flex>
            </v-layout>
            <v-layout
              row
              align-center
              mt-2
            >
              <v-flex
                xs4
                sm3
                md2
                lg2
                xl2
                class="text-xs-left"
              >
                {{ $t('time') }}
              </v-flex>
              <v-flex
                xs8
                sm9
                md10
                lg10
                xl10
                class="text-xs-left"
              >
                {{ form.time.time }}
              </v-flex>
            </v-layout>
            <v-layout
              row
              align-center
              mt-2
            >
              <v-flex
                xs4
                sm3
                md2
                lg2
                xl2
                class="text-xs-left"
              >
                {{ $t('pax') }}
              </v-flex>
              <v-flex
                xs8
                sm9
                md10
                lg10
                xl10
                class="text-xs-left"
              >
                {{ form.pax }}
              </v-flex>
            </v-layout>
            <v-layout
              v-if="discount(form.time.preset_discount)"
              row
              align-center
              mt-2
            >
              <v-flex
                xs4
                sm3
                md2
                lg2
                xl2
                class="text-xs-left"
              >
                {{ $t('discount') }}
              </v-flex>
              <v-flex
                xs8
                sm9
                md10
                lg10
                xl10
                class="text-xs-left"
              >
                {{ discount(form.time.preset_discount) }}
              </v-flex>
            </v-layout>
            <v-layout
              v-if="form.remarks"
              row
              align-center
              mt-2
            >
              <v-flex
                xs4
                sm3
                md2
                lg2
                xl2
                class="text-xs-left"
              >
                {{ $t('remarks') }}
              </v-flex>
              <v-flex
                xs8
                sm9
                md10
                lg10
                xl10
                class="text-xs-left"
              >
                {{ form.remarks }}
              </v-flex>
            </v-layout>
            <div class="save-reservation-text">
              <v-icon
                class="middle"
                size="10"
              >fa fa-exclamation-triangle</v-icon>
              {{ $t('check_reservation_details') }}
            </div>
          </v-card-text>
          <form
            :id="'pay-deposit'"
            :action="paymentTypes['c2p'].dev"
            method="post"
          >
            <input
              type="hidden"
              name="member_id"
              :value="memberId"
            />
            <input
              type="hidden"
              name="loyalty_group_id"
              :value="loyaltyGroupId"
            />
            <input
              type="hidden"
              name="platform_id"
              value="6"
            />
            <input
              type="hidden"
              name="platform_transaction_id"
              value="0"
            />
            <input
              type="hidden"
              name="is_guest"
              value="0"
            />
            <input
              type="hidden"
              name="reference_id"
              value="0"
            />
            <input
              type="hidden"
              name="transaction_amount"
              :value="reservationConfig.voucherPrice"
            />
            <input
              type="hidden"
              name="restaurant_id"
              :value="rid"
            />
            <input
              type="hidden"
              name="account"
              :value="epayAccount"
            />
            <input
              type="hidden"
              name="payment_mode"
              value="c2p"
            />
            <input
              type="hidden"
              name="redirect_url"
              :value="redirectUrl"
            />
            <input
              type="hidden"
              name="notify_url"
              :value="notifyUrl"
            />
            <input
              type="hidden"
              name="hash"
              :value="hash"
            />
          </form>
          <v-card-actions class="pt-0">
            <v-btn
              :color="colors.button1a"
              flat
              block
              @click.native="saveReservation"
            >{{ reservationConfig.enableDeposit ? 'Pay Deposit' : 'Ok' }}</v-btn>
            <v-btn
              :color="colors.button1a"
              flat
              block
              @click.native="saveDialog = false"
            >{{ $t('cancel') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-dialog>
  </div>
</template>

<script>
import moment from 'moment'
import axios from 'axios'
import Form from 'vform'
import HorizontalScroll from 'vue-horizontal-scroll'
import { mapGetters } from 'vuex'
import PageTitle from '~/components/PageTitle'
import BillLine from '~/components/BillLine'
import Datepicker from 'vuejs-datepicker'

export default {
  name: 'reservations-view',
  metaInfo () {
    return { title: this.$t('reservations') }
  },
  components: {
    'page-title': PageTitle,
    'horizontal-scroll': HorizontalScroll,
    'datepicker': Datepicker
  },
  data: () => ({
    dialog: false,
    saveDialog: false,
    loadingDate: false,
    loadingTime: false,
    form: new Form({
      date: null,
      time: null,
      pax: 1,
      remarks: ''
    }),
    dates: {
      from: null,
      to: null
    },
    times: [],
    hash: '',
    slotPax: [1,2,3,4,5,6,7,8,9,10]
  }),
  computed: {
    ...mapGetters('config', [
      'theme', 'colors', 'mode', 'reservationConfig', 'rid', 'config', 'loyaltyGroupId',
      'posType', 'epayAccount', 'paymentTypes'
    ]),
    ...mapGetters('auth', [
      'authMsg', 'reservations'
    ]),
    ...mapGetters('member', [
      'profile', 'memberId', 'isMember'
    ]),
    myLayoutStyle () {
      return {
        marginLeft: '-8px',
        marginRight: '-8px',
        marginBottom: '-16px',
        backgroundColor: this.theme.bg_color
      }
    },
    titleStyle () {
      return {
        fontSize: '20px',
        color: this.theme.orders_title_color
      }
    },
    nameStyle () {
      return {
        color: this.theme.orders_name_color
      }
    },
    cardTitleStyle () {
      return {
        color: this.theme.address_text_color,
        fontWeight: 800
      }
    },
    cardTextStyle () {
      return {
        paddingTop: '0',
        color: this.theme.address_text_color
      }
    },
    isLoading () {
      return this.loadingDate || this.loadingTime
    },
    memberId () {
      if (this.isMember) {
        return this.profile.loyalty_member_id
      }
      return 0 // this.guestId
    },
    redirectUrl () {
      return window.config.url + '/reservations'
    },
    notifyUrl () {
      // if epay/c2p payment success, this url will add reservation
      let data = JSON.stringify({
        voucher_id: this.reservationConfig.voucherId,
        voucher_price: this.reservationConfig.voucherPrice,
        date: this.form.date,
        time: this.form.time.time,
        pax: this.form.pax,
        preset_discount: this.form.time.preset_discount ? JSON.stringify(this.form.time.preset_discount) : null,
        remarks: this.form.remarks,
        hash: this.hash,
        event_id: this.reservationConfig.eventId,
        cashier_id: this.reservationConfig.cashierId,
        sms_content: this.reservationConfig.sms_content,
        user_id: this.profile.user_id,
        sms: this.reservationConfig.sms_content,
        restaurant_id: this.rid,
        loyalty_group_id: this.loyaltyGroupId
      });

      return window.config.url + '/api/reservation/paid?data=' + data
    },
    cssVars() {
      return {
        '--selected-bg-color': this.theme.toolbar_bg_color,
        '--selected-color': this.theme.toolbar_color,
        '--selected-border-color': this.theme.toolbar_color,
      }
    },
    confirmTitleStyle () {
      return {
        fontWeight: 800,
        fontSize: '18px',
        color: this.theme.toolbar_color,
        background: this.theme.toolbar_bg_color,
      }
    },
    confirmTextStyle () {
      return {
        fontSize: '13px',
        marginTop: '8px',
        fontWeight: 800,
        color: this.theme.toolbar_color,
        background: this.theme.toolbar_bg_color,
      }
    }
  },
  methods: {
    itemText (item) {
      return item.preset_discount ? item.time + ' - ' + parseFloat(item.preset_discount.discount_amount) + '% off' : item.time
    },
    discount (presetDiscount) {
      let discount
      try {
        discount = JSON.parse(presetDiscount)
      } catch (e) {
        discount = presetDiscount
      }
      return discount ? parseFloat(discount.discount_amount) + '% ' : null
    },
    dateTime (d) {
      const d2 = moment(d)
      return {
        date: d2.format('D MMMM YYYY') + ', ' + d2.format('dddd'),
        time: d2.format('h:mm A')
      }
    },
    timeStyle (time) {
      let styles = {
        border: '1px solid rgba(0, 0, 0, 0.54)',
        padding: '6px',
        display: 'inline-block',
        cursor: 'pointer',
        borderRadius: '10px',
        height: '65px'
      }
      if (this.form.time && this.form.time.time === time) {
        styles.fontWeight = 800
        styles.background = this.theme.toolbar_bg_color
        styles.color = this.theme.toolbar_color

      }
      styles.width = this.$mq === 'xs' ? '70px' : '80px'
      return styles
    },
    seedlingStyle (time) {
      if (this.form.time && this.form.time.time === time) {
        return {
          color: this.theme.toolbar_color
        }
      }
    },
    async selectDate (date) {
      this.form.date = moment(date).format('YYYY-MM-DD')
      this.form.time = null
      this.loadingTime = true
      await this.fetchTimes(this.form.date)
      if (this.times.length > 0) this.form.time = this.times[0]
      this.loadingTime = false
    },
    async selectTime (time) {
      this.form.time = time
      let div = $('#'+event.currentTarget.id);
      let pos =  div.offset().left/1.1 + div.outerWidth(true)/2 + $('._horizontal-scroll').scrollLeft() - $('._horizontal-scroll').width()/2;
      $('._horizontal-scroll').scrollLeft(pos)
      await this.getSlotPax(time)
    },
    async fetchTimes (date) {
      try {
        const { data } = await axios.post('/api/restaurant/reservationtimes', {
          restaurant_id: this.rid,
          date: date,
          min_before_reservation: this.reservationConfig.minBeforeReservation
        })
        this.times = data.filter(t => t.avail !== 0 )
        await this.getSlotPax(data[0])
      } catch (e) { }
    },
    async addReservation () {
      this.dialog = true
      this.loadingDate = true
      this.form.date = null
      this.form.time = null
      this.form.pax = 1
      this.form.remarks = ''
      try {
        const { data } = await axios.post('/api/next/days', { days: this.reservationConfig.advancedDays })
        if (data.length > 0) {
          await this.selectDate(data[0].date)
          this.dates = {
            from: new Date(data[data.length - 1].date),
            to: new Date(data[0].date)
          }
        }
      } catch (e) { }
      this.loadingDate = false
    },
    async checkReservation () {
      if (await this.formHasErrors()) return
      this.$bus.$emit('showFullLoading', true)
      await this.fetchTimes(this.form.date)
      const time = this.times.find(t => this.form.time && t.time === this.form.time.time)
      this.$bus.$emit('showFullLoading', false)
      if (!time) {
        this.form.time = null
        this.$store.dispatch('responseMessage', {
          type: 'error',
          title: this.$t('invalid_time'),
          text: this.$t('reselect_time'),
          modal: true
        })
        return
      }
      if(this.reservationConfig.enableDeposit) {
        // Get hash from server
        const { data } = await axios.post('/api/epay/md5', {
          guest_id: 0, // this.guestId
          loyalty_group_id: this.loyaltyGroupId,
          restaurant_id: this.rid,
          amount: this.reservationConfig.voucherPrice ? this.reservationConfig.voucherPrice : 0,
          type: 'reservationDeposit'
        })
        this.hash = data
      }
      this.saveDialog = true
    },
    async saveReservation () {
      this.$bus.$emit('showFullLoading', true)
      let voucher_id,voucher_price = null
      if(this.reservationConfig.enableDeposit && this.reservationConfig.voucherId && this.reservationConfig.voucherPrice) {
        voucher_id = this.reservationConfig.voucherId
        voucher_price = this.reservationConfig.voucherPrice
      }

      if (this.reservationConfig.enableDeposit) {
        await this.payment();
      }
      else {
        await this.$store.dispatch('auth/addReservation', {
          voucher_id: voucher_id,
          voucher_price: voucher_price,
          date: this.form.date,
          time: this.form.time.time,
          pax: this.form.pax,
          preset_discount: this.form.time.preset_discount ? JSON.stringify(this.form.time.preset_discount) : null,
          remarks: this.form.remarks,
          hash: this.hash,
        })

        this.$bus.$emit('showFullLoading', false)
        if (this.authMsg) {
          await this.$store.dispatch('responseMessage', {
            type: 'error',
            title: this.$t('reservation_saved_title'),
            text: this.$t(this.authMsg),
            modal: true
          })
        } else {
          this.saveDialog = false
          this.dialog = false
          await this.$store.dispatch('responseMessage', {
            type: 'success',
            text: this.$t('reservation_saved_success_notice')
          })
        }
      }
    },
    async payment () {
      document.getElementById('pay-deposit').submit()
    },
    getSlotPax(time) {
        const max = (time.avail === -1 || time.avail > this.reservationConfig.maxPaxPerReservation)
          ? this.reservationConfig.maxPaxPerReservation
          : time.avail
        // change pax limit
        this.slotPax = []
        for (let i = 1; i <= max; i ++) {
          this.slotPax.push(i)
        }
    }
  },
  async created () {
    this.$bus.$emit('showFullLoading', true)
    await this.$store.dispatch('auth/fetchReservations')
    this.$bus.$emit('showFullLoading', false)
  }
}
</script>

<style scoped>
.main-title {
  font-weight: 800;
}
.btn {
  margin: 10px;
  padding: 0 16px;
}
.small {
  font-size: 13px;
}
.smaller {
  font-size: 12px;
}
.middle {
  padding: 3px 6px 3px 3px;
}
.grey-color {
  color: #8a8a8a;
}
.date-picker >>> .date-picker-calendar {
  width: 100%;
  height: 330px;
}
.date-picker >>> .date-picker-calendar .selected,
.date-picker >>> .date-picker-calendar .selected:hover {
  background-color: var(--selected-bg-color);
  color: var(--selected-color);
}
.date-picker >>> .date-picker-calendar .today {
  border: 1px solid var(--selected-border-color);
}
.save-reservation-title {
  font-weight: 800;
  font-size: 18px;
}
.save-reservation-text {
  font-size: 13px;
  color: #8a8a8a;
  margin-top: 8px;
}
</style>



// WEBPACK FOOTER //
// src/pages/reservations.vue